// @ts-nocheck
/**
 * Edit Announcement — redesigned to obd3-gui pattern (ScrollPage + PageHeader
 * + Card + PageLoader on hydrate). Backend shape drift preserved: server
 * may return { title, message } or { heading, content }.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { ArrowLeft, Loader2, Megaphone, Save } from 'lucide-react';
import { getApiCall, patchApiCall } from '@/service/httpCall';
import {
  API_AGENT_ANNOUNCEMENT_EDIT,
  API_AGENT_ANNOUNCEMENT_SINGLE,
} from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { PageLoader } from '@/components/feedback/PageLoader';

const AgentAnnouncementEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [heading, setHeading] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchUrl = API_AGENT_ANNOUNCEMENT_SINGLE.replace(':id', id);
    getApiCall(fetchUrl)
      .then((resp: any) => {
        const data = resp?.data?.data || resp?.data || resp;
        if (data) {
          setHeading(data.title || data.heading || '');
          setContent(data.message || data.content || '');
          setStatus(data.status === undefined || data.status === null ? true : Boolean(data.status));
        }
      })
      .catch((err: any) => {
        console.error('API Error Detail:', err.response || err);
        toast.error("Can't load data");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const submitForm = (e?: any) => {
    e?.preventDefault();
    if (!id) {
      toast.error('ID missing!');
      return;
    }
    if (!heading.trim() || !content.trim()) {
      toast.warning('All fields are required');
      return;
    }
    setSaving(true);
    const url = API_AGENT_ANNOUNCEMENT_EDIT.replace(':id', id);
    patchApiCall(url, { heading, content, status })
      .then(() => {
        toast.success('Updated successfully');
        navigate(`/${ROUTES.AGENT_ANNOUNCEMENT.LIST}`);
      })
      .catch((err: any) => {
        toast.error(friendlyApiError(err) || 'Update unsuccessful');
        setSaving(false);
      });
  };

  if (loading) return <PageLoader />;

  return (
    <ScrollPage>
      <PageHeader
        title="Edit Announcement"
        description="Update the heading and content shown to agents."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.AGENT_ANNOUNCEMENT.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={submitForm}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-primary" />
              Announcement
            </CardTitle>
            <CardDescription>Visible to every logged-in agent.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="heading">
                Heading <span className="text-destructive">*</span>
              </Label>
              <Input
                id="heading"
                type="text"
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">
                Content <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="content"
                rows={5}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <div className="space-y-0.5">
                <Label htmlFor="status">Status</Label>
                <p className="text-xs text-muted-foreground">
                  Only active announcements are shown to agents.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {status ? 'Active' : 'Inactive'}
                </span>
                <Switch id="status" checked={status} onCheckedChange={setStatus} />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to={`/${ROUTES.AGENT_ANNOUNCEMENT.LIST}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Updating…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
};

export default AgentAnnouncementEdit;
